import React, { useState } from 'react';
import Modal from 'react-modal';
import WebButton from '../../../../library/webButton';
import WebInput from '../../../../library/webInput';
import { useLoader } from '../../../../../base/context/LoaderProvider';
import { ToastTypes, useMessage } from '../../../../../base/context/MessageProvider';
import { marketPlaceApiService } from '../../../../../service/seller/marketPlaceApiService';
import Image from '../../../../../assets/images/marketPlace/form_image.png';
import './style.css';

const isMobile = window.matchMedia("(max-width: 768px)").matches;

const customStyles = {
  content: {
    top: '55%',
    left: isMobile ? '50%' : '60%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '65vw',
    height: 'auto',
    overflow: 'auto',
    borderRadius: '16px',
    padding: '0',
    border: 'none',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
  },
};

Modal.setAppElement('#root');

export default function ShopifyForm({ modalIsOpen, setIsOpen, handleStores }) {
  const loader = useLoader();
  const message = useMessage();

  const [data, setData] = useState({
    storeDomain: '',
    shopifyApiKey: '',
    shopifyApiSecret: '',
  });

  const [error, setError] = useState({
    storeDomain: '',
    shopifyApiKey: '',
    shopifyApiSecret: '',
  });

  // State for YouTube video - Using your provided URL
  const [embedUrl, setEmbedUrl] = useState('https://www.youtube.com/embed/fEgta2zjlx8');

  const handleOnchange = e => {
    setData({ ...data, [e.target.name]: e.target.value });
    setError({ ...error, [e.target.name]: '' });
  };

  function closeModal() {
    setIsOpen(false);
  }

  // Function to open video in modal (plays the video)
  const openVideoInSection = () => {
    // When user clicks on video, ensure it's playable
    // Add autoplay parameter to the embed URL
    const autoplayUrl = `${embedUrl}?autoplay=1`;
    setEmbedUrl(autoplayUrl);

    // Show a subtle message (optional)
    message.showToast('Playing video...', ToastTypes.INFO);
  };

  const validate = () => {
    let result = true;
    if (!data.storeDomain.length) {
      setError({ ...error, storeDomain: 'Domain is required' });
      result = false;
    } else {
      // Validate shop domain format (xxx.myshopify.com)
      const shopDomainPattern = /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]\.myshopify\.com$/;
      const cleanDomain = data.storeDomain.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
      if (!shopDomainPattern.test(cleanDomain)) {
        setError({ ...error, storeDomain: 'Invalid domain format. Must be: xxx.myshopify.com' });
        result = false;
      }
    }
    if (!data.shopifyApiKey.length) {
      setError({ ...error, shopifyApiKey: 'API Key is required' });
      result = false;
    }
    if (!data.shopifyApiSecret.length) {
      setError({ ...error, shopifyApiSecret: 'API Secret is required' });
      result = false;
    }
    return result;
  };

  const [isConnecting, setIsConnecting] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    if (validate()) {
      // Normalize shop domain
      let shopDomain = data.storeDomain.trim().toLowerCase();
      shopDomain = shopDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');
      
      // If user entered just shop name, append .myshopify.com
      if (!shopDomain.includes('.')) {
        shopDomain = `${shopDomain}.myshopify.com`;
      }

      try {
        setIsConnecting(true);
        loader.showLoading(true);
        
        // Fetch OAuth URL via authenticated API call with API Key and Secret
        const response = await marketPlaceApiService.getShopifyOAuthUrl(shopDomain, data.shopifyApiKey, data.shopifyApiSecret);
        
        if (response.status && response.status.code === 200 && response.data && response.data.oauthUrl) {
          const oauthUrl = response.data.oauthUrl;
          
          // Keep loading visible and redirect in the same window
          // After OAuth completes, Shopify will redirect back to our callback,
          // which will then redirect to the frontend page
          window.location.href = oauthUrl;
        } else {
          setIsConnecting(false);
          loader.showLoading(false);
          message.showToast(response.status?.message || 'Failed to initiate Shopify connection', ToastTypes.ERROR);
        }
      } catch (err) {
        setIsConnecting(false);
        loader.showLoading(false);
        if (err.status && err.status.code === 400) {
          message.showToast(err.data?.message || 'Invalid shop domain or store already connected', ToastTypes.ERROR);
        } else {
          message.showToast(err?.message || 'Failed to connect Shopify store. Please try again.', ToastTypes.ERROR);
        }
      }
    }
  };

  return (
    <Modal isOpen={modalIsOpen} onRequestClose={!isConnecting ? closeModal : undefined} style={customStyles} contentLabel="Shopify Form Modal">
      {isConnecting && (
        <div className="shopify_connecting_overlay">
          <div className="shopify_connecting_content">
            <div className="shopify_connecting_spinner"></div>
            <p className="shopify_connecting_text">Connecting to Shopify...</p>
            <p className="shopify_connecting_subtext">Please wait while we establish the connection</p>
          </div>
        </div>
      )}
      <div className="shopify_modal_container">
        <button className="shopify_modal_close" onClick={closeModal} disabled={isConnecting}>
          ×
        </button>
        
        <div className="shopify_marketplace_form_box">
          <div className="shopify_form_left">
            {/* <img src={Image} className="shopify_marketplace_img" alt="Shopify Integration" /> */}

            <form className="shopify_marketplace_form" onSubmit={handleSubmit}>
              {/* App URL Note Section */}
              <div className="shopify_app_url_note">
                <div className="shopify_note_header">
                  <span className="shopify_note_icon">📌</span>
                  <span className="shopify_note_title">Note: App URL Required</span>
                </div>
                <p className="shopify_note_text">
                  When setting up your Shopify app, use this App URL:
                </p>
                <div className="shopify_app_url_container">
                  <code className="shopify_app_url">https://importerr.com/api/user/shopify/callback</code>
                  <button
                    type="button"
                    className="shopify_copy_button"
                    onClick={() => {
                      navigator.clipboard.writeText('https://importerr.com/api/user/shopify/callback');
                      message.showToast('App URL copied!', ToastTypes.SUCCESS);
                    }}
                    title="Copy to clipboard"
                  >
                    📋 Copy
                  </button>
                </div>
              </div>

              <h2 className="shopify_marketplace_form_head">Shopify Seller API Integration</h2>

              <div className="shopify_input_group">
                <WebInput 
                  id="domainInput" 
                  type="text" 
                  label="STORE DOMAIN" 
                  name="storeDomain" 
                  value={data.storeDomain} 
                  error={error.storeDomain} 
                  onChange={handleOnchange}
                  placeholder="xxx.myshopify.com"
                />
                <p className="shopify_input_hint">
                  Enter your Shopify store domain (e.g., mystore.myshopify.com)
                </p>
              </div>

              <div className="shopify_input_group">
                <WebInput 
                  id="apiKeyInput" 
                  type="text" 
                  label="SHOPIFY API KEY (Client ID)" 
                  name="shopifyApiKey" 
                  value={data.shopifyApiKey} 
                  error={error.shopifyApiKey} 
                  onChange={handleOnchange}
                  placeholder="Enter your Shopify App Client ID"
                />
              </div>

              <div className="shopify_input_group">
                <WebInput 
                  id="apiSecretInput" 
                  type="password" 
                  label="SHOPIFY API SECRET (Client Secret)" 
                  name="shopifyApiSecret" 
                  value={data.shopifyApiSecret} 
                  error={error.shopifyApiSecret} 
                  onChange={handleOnchange}
                  placeholder="Enter your Shopify App Client Secret"
                />
              </div>
            </form>
          </div>

          {/* Video Section */}
          <div className="shopify_video_section">
            <h3 className="shopify_video_title">How to Connect Shopify</h3>
            <div className="shopify_video_preview" onClick={openVideoInSection}>
              <iframe
                src={embedUrl}
                title="Shopify Integration Tutorial"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="shopify_video_iframe"
                referrerPolicy="strict-origin-when-cross-origin"
              ></iframe>
            </div>

            <div className="shopify_video_button_container">
              <WebButton 
                className="shopify_connect_button" 
                text="Connect To Shopify" 
                onClick={e => handleSubmit(e)} 
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
